/**
 * WordPress dependencies
 */
import apiFetch from '@safe-wordpress/api-fetch';
import {
	Button,
	CheckboxControl,
	ExternalLink,
} from '@safe-wordpress/components';
import { createInterpolateElement } from '@safe-wordpress/element';
import { _x, sprintf } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { usePageAttribute } from '@nab/data';
import { addFreeTracker } from '@nab/utils';

/**
 * Internal dependencies
 */
import './style.scss';

export const PolicyBody = (): JSX.Element => {
	const [ isPluginBeingInitialized, initPluginInAws ] =
		usePluginInitializer();
	const [ status, setStatus ] = usePageAttribute(
		'welcome/status',
		'unchecked-policy'
	);

	const isPolicyAccepted = 'unchecked-policy' !== status;
	const togglePolicy = ( enabled: boolean ) =>
		setStatus( enabled ? 'checked-policy' : 'unchecked-policy' );

	return (
		<div className="nab-policy-body">
			<p className="nab-policy-body__intro">
				{ createInterpolateElement(
					sprintf(
						/* translators: %s: Plugin name (Nelio A/B Testing). */
						_x( '%s is almost ready!', 'text', 'nelio-ab-testing' ),
						'<strong>Nelio A/B Testing</strong>'
					),
					{ strong: <strong /> }
				) }
			</p>

			<div className="nab-policy-body__policy">
				<CheckboxControl
					label={ createInterpolateElement(
						_x(
							'Please accept our <tac>Terms and Conditions</tac> and <policy>Privacy Policy</policy> to start using Nelio A/B Testing in this site.',
							'user',
							'nelio-ab-testing'
						),
						{
							tac: (
								// @ts-expect-error children prop is set by createInterpolateComponent.
								<ExternalLink
									href={ addFreeTracker(
										'https://neliosoftware.com/legal-information/nelio-ab-testing-terms-conditions/'
									) }
								/>
							),
							policy: (
								// @ts-expect-error children prop is set by createInterpolateComponent.
								<ExternalLink
									href={ addFreeTracker(
										'https://neliosoftware.com/privacy-policy-cookies/'
									) }
								/>
							),
						}
					) }
					disabled={ isPluginBeingInitialized }
					checked={ isPolicyAccepted }
					onChange={ togglePolicy }
				/>
			</div>

			<div className="nab-policy-body__actions">
				<Button
					className="nab-policy-body__start"
					variant="primary"
					disabled={ ! isPolicyAccepted }
					isBusy={ isPluginBeingInitialized }
					onClick={ () => initPluginInAws() }
				>
					{ isPluginBeingInitialized
						? _x(
								'Initializing plugin…',
								'text',
								'nelio-ab-testing'
						  )
						: _x( 'Continue »', 'command', 'nelio-ab-testing' ) }
				</Button>
			</div>
		</div>
	);
};

// =====
// HOOKS
// =====

type Response = {
	readonly quotaBoost?: {
		readonly value: number;
		readonly expirationDate: string;
	};
};

const usePluginInitializer = () => {
	const [ status, setStatus ] = usePageAttribute(
		'welcome/status',
		'unchecked-policy'
	);
	const [ __, setBoostQuota ] = usePageAttribute( 'welcome/quota-boost', {
		value: 0,
		expirationDate: '',
	} );

	const init = () => {
		setStatus( 'initializing-plugin' );
		void apiFetch< Response >( {
			path: '/nab/v1/site/free',
			method: 'POST',
		} ).then( ( response ) => {
			if ( response.quotaBoost ) {
				setBoostQuota( response.quotaBoost );
			}
			setStatus( 'homepage-scan' );
		} );
	};
	return [ 'initializing-plugin' === status, init ] as const;
};
